import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { lkAuth } from "@flame/shared/api";

/**
 * LK Login page (auth-v2, 2026-05-12).
 *
 * Bot deeplink flow:
 *  1. POST /v1/lk/auth/bot-deeplink/start → { code, deeplink_url, web_fallback_url }
 *  2. Navigate to tg://… (opens Telegram). On desktop without TG app, fall back to t.me/…
 *  3. Poll /v1/lk/auth/bot-deeplink/poll/{code} every 2s for ≤5 min.
 *  4. On status="ok": cookie is set. totp_required → /totp/verify, else /.
 *  5. On status="expired": show error + reset.
 *
 * @AlertVPSV_Bot is the LK bot (NOT admin bot).
 */
export function LoginPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pollingCode, setPollingCode] = useState<string | null>(null);
  const [webFallback, setWebFallback] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function copyFallback() {
    if (!webFallback) return;
    try {
      await navigator.clipboard.writeText(webFallback);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // No clipboard API (older Safari, insecure context) — the
      // adjacent <a href={webFallback}> remains a working fallback.
    }
  }

  async function handleLogin() {
    setBusy(true);
    setError(null);
    // Mobile vs desktop deeplink strategy — they behave very differently:
    //
    //   Mobile: setting window.location.href = "tg://..." is an OS hand-off
    //   (like mailto:). The browser does NOT navigate away; the OS opens
    //   Telegram and the LK page keeps running so the polling effect can
    //   see /start succeed and seal the cookie.
    //
    //   Desktop: tg:// usually has no protocol handler → silent no-op. We
    //   open a new tab to https://t.me/... instead. The popup must be
    //   pre-opened SYNCHRONOUSLY with the click (browsers ignore popups
    //   that fire after an awaited API call), then redirected after the
    //   API returns.
    const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(
      navigator.userAgent,
    );
    const popup = isMobile ? null : window.open("about:blank", "_blank");
    try {
      const r = await lkAuth.botDeeplinkStart();
      setPollingCode(r.code);
      setWebFallback(r.web_fallback_url);
      if (isMobile) {
        window.location.href = r.deeplink_url;
      } else if (popup && !popup.closed) {
        popup.location.href = r.web_fallback_url;
      } else {
        // Desktop popup blocked — fall back to same-window navigation.
        window.location.href = r.web_fallback_url;
      }
    } catch {
      if (popup && !popup.closed) popup.close();
      setError("Не удалось начать вход. Попробуйте ещё раз.");
      setBusy(false);
    }
  }

  useEffect(() => {
    if (!pollingCode) return;
    let interval: ReturnType<typeof setInterval> | null = null;
    let timeout: ReturnType<typeof setTimeout> | null = null;

    const stop = () => {
      if (interval) clearInterval(interval);
      if (timeout) clearTimeout(timeout);
      interval = null;
      timeout = null;
    };

    interval = setInterval(async () => {
      try {
        const r = await lkAuth.botDeeplinkPoll(pollingCode);
        if (r.status === "ok") {
          stop();
          await queryClient.invalidateQueries({ queryKey: ["me"] });
          if (r.totp_required) {
            void navigate("/totp/verify", { replace: true });
          } else {
            void navigate("/", { replace: true });
          }
        } else if (r.status === "expired") {
          stop();
          setPollingCode(null);
          setWebFallback(null);
          setBusy(false);
          setError("Ссылка устарела. Нажмите «Войти» ещё раз.");
        }
      } catch {
        // Network blip — keep polling.
      }
    }, 2000);

    timeout = setTimeout(() => {
      stop();
      setPollingCode(null);
      setWebFallback(null);
      setBusy(false);
      setError("Время ожидания истекло. Попробуйте ещё раз.");
    }, 5 * 60 * 1000);

    return stop;
  }, [pollingCode, navigate, queryClient]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-bg px-4 py-12">
      {/* Brand */}
      <div className="mb-10 flex flex-col items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-surface shadow-card">
          <svg width="28" height="28" viewBox="0 0 32 32" fill="none" aria-hidden>
            <rect width="32" height="32" rx="8" fill="#FF6B00" />
            <path d="M10 22V10l12 6-12 6Z" fill="white" />
          </svg>
        </div>
        <h1 className="text-2xl font-semibold tracking-tight text-fg">FlameVPN</h1>
        <p className="text-sm text-fg-muted">Личный кабинет</p>
      </div>

      {/* Card */}
      <div className="w-full max-w-sm rounded-xl bg-surface p-8 shadow-card">
        <h2 className="mb-1 text-base font-semibold text-fg">
          Вход через Telegram
        </h2>
        <p className="mb-6 text-sm leading-relaxed text-fg-muted">
          FlameVPN работает по инвайтам. Нажмите кнопку ниже — откроется Telegram-бот, нажмите Start, и вы войдёте автоматически.
        </p>

        {/* CTA button */}
        <button
          type="button"
          onClick={() => void handleLogin()}
          disabled={busy}
          className="flex w-full items-center justify-center gap-2.5 rounded-md bg-accent px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-[var(--color-accent-hover)] disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          {busy ? (
            <span aria-hidden className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
          ) : (
            <TelegramIcon />
          )}
          <span>{busy ? "Ожидание подтверждения…" : "Войти через Telegram"}</span>
        </button>

        {/* Polling hint */}
        {busy && pollingCode && (
          <div className="mt-4 rounded-md bg-surface-elevated px-3 py-2.5 text-center text-xs text-fg-muted">
            <p>
              Откройте{" "}
              {webFallback ? (
                <a
                  href={webFallback}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-fg underline decoration-fg/30 underline-offset-2 hover:text-accent hover:decoration-accent"
                >
                  @AlertVPSV_Bot
                </a>
              ) : (
                <span className="font-medium text-fg">@AlertVPSV_Bot</span>
              )}{" "}
              и нажмите <span className="font-medium text-fg">Start</span>.
            </p>
            {webFallback && (
              <p className="mt-1.5">
                Не открылось?{" "}
                <button
                  type="button"
                  onClick={() => void copyFallback()}
                  className="text-accent hover:underline"
                >
                  {copied ? "Скопировано ✓" : "Скопировать ссылку"}
                </button>
              </p>
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <p
            role="alert"
            className="mt-4 rounded-md border border-danger/30 bg-[var(--color-danger-muted)] px-3 py-2 text-xs text-danger"
          >
            {error}
          </p>
        )}

        {/* Invite hint */}
        <p className="mt-6 text-center text-xs text-fg-subtle">
          Нет инвайта?{" "}
          <a
            href="https://t.me/AlertVPSV_Bot"
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent transition-colors hover:text-accent-hover"
          >
            Напишите боту
          </a>
        </p>
      </div>
    </div>
  );
}

function TelegramIcon() {
  // Official Telegram glyph, simplified.
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.24 3.64 11.95c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" />
    </svg>
  );
}
